import React, {PureComponent} from 'react';
import {FastField, Field, ErrorMessage, FieldArray} from 'formik';
import {FormattedMessage} from 'react-intl';
import get from 'lodash/get';
import omit from 'lodash/omit';
import classNames from 'classnames';
import messages from '../../../../../res/translations/messages';
import FormGroup from '../FormGroup';
import CommonFields from './CommonFields';
import Error from '../../../Error';
import ExclusiveChoiceType from '../ExclusiveChoiceType';
import {generateExclusiveChoiceId} from '../../../../services/idHelpers';
import {Alert} from 'reactstrap';
import SurveyEditorRangeColors from '../../SurveyEditorRangeColors';
import SurveyEditorRatingPreview from '../../SurveyEditorRatingPreview';

class Number2Type extends PureComponent {
    constructor(props) {
        super(props);
        this.handleNpsChange = this.handleNpsChange.bind(this);
        this.handleExclusiveChoiceChange = this.handleExclusiveChoiceChange.bind(this);
    }

    handleNpsChange(e) {
        const {form, name} = this.props;
        if (e.target.checked) {
            form.setFieldValue(`${name}.min`, 0);
            form.setFieldValue(`${name}.max`, 10);
            form.setFieldValue(`${name}.minLabel`, '');
            form.setFieldValue(`${name}.maxLabel`, '');
        } else {
            form.setFieldValue(`${name}.min`, 1);
        }
        form.setFieldValue(`${name}.nps`, e.target.checked);
    }

    handleExclusiveChoiceChange(e) {
        const {form, name} = this.props;
        form.setFieldValue(`${name}.exclusiveChoiceEnabled`, e.target.checked);
        if (e.target.checked) {
            if (!get(form.values, `${name}.exclusiveChoice.id`)) {
                form.setFieldValue(`${name}.exclusiveChoice.id`,
                    generateExclusiveChoiceId(get(form.values, `${name}.max`)));
                form.setFieldValue(`${name}.exclusiveChoice.value`, '');
            }
        } else {
            omit(form.values, [`${name}.exclusiveChoice`]);
        }
    }

    render() {
        const {name, form, previousPages} = this.props;
        const initialValues = get(form.initialValues, name);
        if (initialValues) {
            initialValues.exclusiveChoiceEnabled = !!initialValues.exclusiveChoiceEnabled; // it could not be initialized. it should be done on server side
        }

        return (
            <>
                <CommonFields name={name} form={form} required={true} previousPages={previousPages} />
                <div className="row">
                    <div className="col">
                        <FastField name={`${name}.nps`}>
                            {({field}) => (
                                <div className="custom-control custom-checkbox">
                                    <input type="checkbox" name={field.name} id={field.name} className="custom-control-input"
                                        checked={field.value} onChange={this.handleNpsChange} />
                                    <label className="custom-control-label" htmlFor={field.name}>
                                        <FormattedMessage {...messages.surveys.form.label.nps} />
                                    </label>
                                </div>
                            )}
                        </FastField>
                    </div>
                </div>
                <div className="row">
                    <FormGroup name={`${name}.min`} label={messages.surveys.form.label.min} size={3} tooltip={messages.surveys.form.tooltip.min}>
                        <Field id={`${name}.min`}
                            name={`${name}.min`}
                            type="number"
                            step={get(form.values, `${name}.componentType`) === 'rating' ? 1 : 'any'}
                            readOnly={get(form.values, `${name}.nps`)}
                            className={classNames('form-control',
                                {error: get(form.errors, `${name}.min`) && get(form.touched, `${name}.min`)})} />
                        <ErrorMessage name={`${name}.min`}>{(message) => <Error reason={message.key} reasonParams={message.params} />}</ErrorMessage>
                    </FormGroup>
                    <FormGroup name={`${name}.minLabel`} label={messages.surveys.form.label.minLabel} size={6}
                        tooltip={messages.surveys.form.tooltip.number2.minLabel}>
                        <Field id={`${name}.minLabel`} name={`${name}.minLabel`} type="text" readOnly={get(form.values, `${name}.nps`)}
                            className={classNames('form-control',
                                {error: get(form.errors, `${name}.minLabel`) && get(form.touched, `${name}.minLabel`)})} />
                        <ErrorMessage name={`${name}.minLabel`}>{(message) => <Error reason={message.key} reasonParams={message.params} />}</ErrorMessage>
                    </FormGroup>
                </div>
                <div className="row">
                    <FormGroup name={`${name}.max`} label={messages.surveys.form.label.max} size={3} tooltip={messages.surveys.form.tooltip.max}>
                        <Field id={`${name}.max`}
                            name={`${name}.max`}
                            type="number"
                            step={get(form.values, `${name}.componentType`) === 'rating' ? 1 : 'any'}
                            readOnly={get(form.values, `${name}.nps`)}
                            className={classNames('form-control',
                                {error: get(form.errors, `${name}.max`) && get(form.touched, `${name}.max`)})} />
                        <ErrorMessage name={`${name}.max`}>{(message) => <Error reason={message.key} reasonParams={message.params} />}</ErrorMessage>
                    </FormGroup>
                    <FormGroup name={`${name}.maxLabel`} label={messages.surveys.form.label.maxLabel} size={6}
                        tooltip={messages.surveys.form.tooltip.number2.maxLabel}>
                        <Field id={`${name}.maxLabel`} name={`${name}.maxLabel`} type="text" readOnly={get(form.values, `${name}.nps`)}
                            className={classNames('form-control',
                                {error: get(form.errors, `${name}.maxLabel`) && get(form.touched, `${name}.maxLabel`)})} />
                        <ErrorMessage name={`${name}.minLabel`}>{(message) => <Error reason={message.key} reasonParams={message.params} />}</ErrorMessage>
                    </FormGroup>
                </div>
                {get(form.values, `${name}.componentType`) !== 'rating' && <div className="row">
                    <FormGroup name={`${name}.step`} label={messages.surveys.form.label.step} size={3} tooltip={messages.surveys.form.tooltip.step}>
                        <FastField id={`${name}.step`} name={`${name}.step`} type="number" step="any"
                            className={classNames('form-control',
                                {error: get(form.errors, `${name}.step`) && get(form.touched, `${name}.step`)})} />
                        <ErrorMessage name={`${name}.step`}>{(message) => <Error reason={message.key} reasonParams={message.params} />}</ErrorMessage>
                    </FormGroup>
                    <FormGroup name={`${name}.interval`} label={messages.surveys.form.label.interval} size={3} tooltip={messages.surveys.form.tooltip.interval}>
                        <FastField id={`${name}.interval`} name={`${name}.interval`} type="number" step="any"
                            className={classNames('form-control',
                                {error: get(form.errors, `${name}.interval`) && get(form.touched, `${name}.interval`)})} />
                        <ErrorMessage name={`${name}.interval`}>{(message) => <Error reason={message.key} reasonParams={message.params} />}</ErrorMessage>
                    </FormGroup>
                </div>}
                {get(form.values, `${name}.componentType`) === 'rating' && <div className="row" >
                    <FormGroup name={`${name}.rangesColors`} size={5}
                        label={messages.surveys.form.label.rangesColors.label}
                        tooltip={messages.surveys.form.tooltip.number2.rangesColors}>
                        <FieldArray name={`${name}.rangesColors`} component={SurveyEditorRangeColors} />
                    </FormGroup>
                    <div className="row">
                        <div className="col" >
                            <SurveyEditorRatingPreview min={get(form.values, `${name}.min`)}
                                max={get(form.values, `${name}.max`)}
                                rangesColors={get(form.values, `${name}.rangesColors`)} />
                        </div>
                    </div>
                </div>}
                {get(form.values, `${name}.nps`)? null : null }
                <div className="row">
                    <div className="col">
                        <FastField name={`${name}.exclusiveChoiceEnabled`}>
                            {({field}) => (
                                <div className="custom-control custom-checkbox">
                                    <input type="checkbox" name={field.name} id={field.name} className="custom-control-input"
                                        checked={field.value} onChange={this.handleExclusiveChoiceChange} />
                                    <label className="custom-control-label" htmlFor={field.name}>
                                        <FormattedMessage {...messages.surveys.form.label.exclusiveChoiceEnabled} />
                                    </label>
                                </div>
                            )}
                        </FastField>
                        <ErrorMessage name={`${name}.exclusiveChoiceEnabled`}>
                            {(message) => <Error reason={message.key} reasonParams={message.params} />}
                        </ErrorMessage>
                    </div>
                </div>
                {get(form.values, `${name}.exclusiveChoiceEnabled`) && get(form.values, `${name}.nps`)?
                    <Alert color="warning mt-2">
                        <FormattedMessage {...messages.surveys.form.label.exclusiveNPSWarning} />
                    </Alert> : null}
                {get(form.values, `${name}.exclusiveChoiceEnabled`)? <ExclusiveChoiceType form={form} name={name} /> : null}
            </>
        );
    }
}

export default Number2Type;
